import * as Api from '@ViewModels';
import { useCallback } from 'react';
import { useStepSave, type BaseStepOverrides } from './useStepSaveFactory';
import type { useAutoSave } from './useAutoSave';

interface UseEmailLikeStepSaveProps<TStep extends Api.IEmailAutomationStep> {
	step: TStep | null;
	stepId: string | undefined;
	stepType: Api.AutomationStepType;
	templateId: string | undefined;
	name: string;
	tagToAddOnClick: string;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	autoSave: ReturnType<typeof useAutoSave>;
	/**
	 * Fields belonging to one specific email-like step, merged over the shared ones.
	 * Memoize it — it feeds the save callback's dependencies.
	 */
	extraFields?: Partial<TStep>;
}

export interface UseEmailLikeStepSaveOverrides extends BaseStepOverrides {
	newName?: string;
	newSendFrom?: Api.ISendFromOptions | null;
	newTagToAddOnClick?: string;
	newSendToSender?: boolean | null;
	newContactFilter?: Api.IContactsFilterRequest | null;
	newReminderOptions?: Api.IMessageReminderOptions | null;
}

interface UseEmailLikeStepSaveResult {
	save: (overrides?: UseEmailLikeStepSaveOverrides) => Promise<void>;
}

/**
 * Shared save hook for the email-like step renderers (Email, HtmlNewsletter, AI email).
 *
 * They share the same save shape — name, tagToAddOnClick, contactFilter, sendFrom and
 * sendToSender — and differ only in the `stepType` they persist under and whatever
 * `extraFields` the specific step owns.
 */
export function useEmailLikeStepSave<TStep extends Api.IEmailAutomationStep>({
	step,
	stepId,
	stepType,
	templateId,
	name,
	tagToAddOnClick,
	skipStepContactFilter,
	autoSave,
	extraFields,
}: UseEmailLikeStepSaveProps<TStep>): UseEmailLikeStepSaveResult {
	const buildUpdatedStep = useCallback(
		(currentStep: TStep | null, overrides: UseEmailLikeStepSaveOverrides): Partial<TStep> => {
			const effectiveName = overrides.newName ?? name;
			const effectiveTagToAddOnClick = overrides.newTagToAddOnClick ?? tagToAddOnClick;
			const effectiveContactFilter =
				overrides.newContactFilter !== undefined ? overrides.newContactFilter : skipStepContactFilter;

			const updatedStep: Partial<TStep> = {
				...currentStep,
				...extraFields,
				name: effectiveName || currentStep?.name || '',
				tagToAddOnClick: effectiveTagToAddOnClick || '',
				contactFilter: effectiveContactFilter,
			};

			if (overrides.newSendFrom !== undefined) {
				updatedStep.sendFromOptions = overrides.newSendFrom;
			}

			if (overrides.newSendToSender !== undefined) {
				if (!updatedStep.options) {
					updatedStep.options = {
						noteVisibility: 'all',
						saveAsNote: true,
					};
				}
				updatedStep.options = {
					...updatedStep.options,
					sendToSender: !!overrides.newSendToSender,
				};
			}

			if (overrides.newReminderOptions !== undefined) {
				updatedStep.reminderOptions = overrides.newReminderOptions;
			}

			return updatedStep;
		},
		[name, tagToAddOnClick, skipStepContactFilter, extraFields]
	);

	return useStepSave<TStep, UseEmailLikeStepSaveOverrides>({
		step,
		stepId,
		stepType,
		templateId,
		autoSave,
		buildUpdatedStep,
	});
}
